import {keys, filter, reduce} from 'lodash';
import React, {forwardRef, useState, useMemo, ForwardRefRenderFunction, useCallback} from 'react';
import {DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {Collapse, Checkbox} from 'acud';
import HooksForm from '../../../../components/biz/HooksForm';
import useForm from '../../../../hooks/biz/form';

import {SortTableItem} from './SortTableItem';

import './index.less';

const {Panel} = Collapse;
const CheckboxGroup = Checkbox.Group;
const controls = (props: any, _, customFunctions: any) => {
    const {isGroup, cloumnGroupInfo = {}, selectedCloumnInfo} = props || {};
    const groupNames = filter(keys(cloumnGroupInfo), n => n !== 'all');

    return [
        {
            type: 'custom',
            name: 'attributes',
            label: '导出字段',
            content: isGroup ? (
                <Collapse defaultActiveKey={groupNames}>
                    {
                        groupNames.map(groupName => {
                            const handleDragEnd = (event: any) => {
                                const {active, over} = event;
                                if (active.id !== over.id) {
                                    // eslint-disable-next-line max-len
                                    const oldIndex = cloumnGroupInfo[groupName].findIndex(item => item.value === active.id);
                                    // eslint-disable-next-line max-len
                                    const newIndex = cloumnGroupInfo[groupName].findIndex(item => item.value === over.id);

                                    const newItems = arrayMove(cloumnGroupInfo[groupName], oldIndex, newIndex);
                                    customFunctions.onReorder(groupName, newItems);
                                }
                            };

                            return (
                                <Panel
                                    key={groupName}
                                    header={
                                        <Checkbox
                                            indeterminate={selectedCloumnInfo?.[groupName]?.indeterminate}
                                            checked={selectedCloumnInfo?.[groupName]?.checkAll}
                                            onChange={customFunctions.onCheckAllChange(groupName)}
                                        >
                                            {groupName}
                                        </Checkbox>
                                    }
                                >
                                    <Checkbox
                                        className='attributes-checkall'
                                        indeterminate={selectedCloumnInfo?.[groupName]?.indeterminate}
                                        checked={selectedCloumnInfo?.[groupName]?.checkAll}
                                        onChange={customFunctions.onCheckAllChange(groupName)}
                                    >
                                        {groupName}
                                    </Checkbox>
                                    <div className="attributes-list">
                                        <DndContext
                                            sensors={useSensors(
                                                useSensor(PointerSensor),
                                                useSensor(KeyboardSensor, {
                                                    coordinateGetter: sortableKeyboardCoordinates
                                                })
                                            )}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={cloumnGroupInfo[groupName].map(item => item.value)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {cloumnGroupInfo[groupName].map(option => {
                                                    // eslint-disable-next-line max-len
                                                    const isChecked = selectedCloumnInfo?.[groupName]?.checkedList?.includes(option.value);
                                                    // eslint-disable-next-line max-len
                                                    const handleChange = (e: any) => {
                                                        const checked = e.target.checked;
                                                        // eslint-disable-next-line max-len
                                                        const currentList = selectedCloumnInfo?.[groupName]?.checkedList || [];
                                                        // eslint-disable-next-line max-len
                                                        const newList = checked ? [...currentList, option.value] : currentList.filter(v => v !== option.value);
                                                        customFunctions.onChange(groupName)(newList);
                                                    };
                                                    return (
                                                        <SortTableItem
                                                            key={option.value}
                                                            id={option.value}
                                                            dragHandle={<>≡</>}
                                                        >
                                                            <div className="draggable-item">
                                                                <Checkbox
                                                                    value={option.value}
                                                                    checked={isChecked}
                                                                    onChange={handleChange}
                                                                    disabled={option.disabled}
                                                                >
                                                                    {option.label}
                                                                </Checkbox>
                                                            </div>
                                                        </SortTableItem>
                                                    );
                                                })
                                                }
                                            </SortableContext>
                                        </DndContext>
                                    </div>
                                    {/* <CheckboxGroup
                                        options={cloumnGroupInfo[groupName]}
                                        value={selectedCloumnInfo?.[groupName]?.checkedList}
                                        onChange={customFunctions.onChange(groupName)}
                                    /> */}
                                </Panel>
                            );
                        })
                    }
                </Collapse>
            ) : <CheckboxGroup
                options={cloumnGroupInfo.all}
                value={selectedCloumnInfo?.all?.checkedList}
                onChange={customFunctions.onChange('all')}
            />
        }
    ];
};

interface TagFormProps {
    payload: {
        cloumnKeys: any[];
        setCloumnKeys?: any;
    },
    onSuccess?: () => void
}
const AttributesForm: ForwardRefRenderFunction<unknown, TagFormProps> = (props, ref) => {
    const {payload, onSuccess} = props;
    const {cloumnKeys = [], setCloumnKeys} = payload;
    const [selectedCloumnInfo, setSelectedCloumnInfo] = useState<any>({});

    const isGroup = useMemo(() => cloumnKeys.some((item: any) => !!item.groupName), [cloumnKeys]);
    const defaultCloumnGroupInfo = useMemo(() => {
        let info = {
            all: {
                checkAll: false,
                indeterminate: false,
                checkedList: []
            }
        };
        return cloumnKeys.reduce((res, item: any, index) => {
            if (isGroup && item.groupName) {
                res[item.groupName] = [
                    ...(res[item.groupName] || []),
                    {...item, label: item.title, value: item.key, disabled: item.isUniq}
                ];
                if (!info[item.groupName]) {
                    info[item.groupName] = {
                        checkAll: false,
                        indeterminate: false,
                        checkedList: []
                    };
                }
            }
            res.all = [...(res.all || []), {...item, label: item.title, value: item.key}];
            if (index === cloumnKeys.length - 1) {
                setSelectedCloumnInfo(reduce(Object.keys(info), (obj, itemKey: string) => {
                    const checkedList = reduce(res[itemKey] || [], (rCheck, itemCheck) => {
                        if (itemCheck.isUniq || itemCheck.show) {
                            rCheck.push(itemCheck.value);
                        }
                        return rCheck;
                    }, [] as string[]);
                    obj[itemKey] = {
                        checkAll: checkedList.length === res[itemKey].length,
                        indeterminate: checkedList.length && checkedList.length < res[itemKey].length,
                        checkedList
                    };
                    return obj;
                }, {} as {[key: string]: any}));
            }
            return res;
        }, {});
    }, [isGroup, cloumnKeys]);
    const [cloumnGroupInfo, setCloumnGroupInfo] = useState(defaultCloumnGroupInfo);

    const onCheckAllChange = useCallback((groupName: string) => (e: any) => {
        setSelectedCloumnInfo(info => ({
            ...info,
            [groupName]: {
                checkAll: e.target.checked,
                indeterminate: false,
                checkedList: reduce(cloumnGroupInfo[groupName] || [], (res, item: {value: string, isUniq: boolean}) => {
                    if (e.target.checked || !e.target.checked && item.isUniq) {
                        res.push(item.value);
                    }
                    return res;
                }, [] as string[])
            }
        }));
    }, [setSelectedCloumnInfo, cloumnGroupInfo]);

    const onChange = useCallback((groupName: string) => (list: string[]) => {
        setSelectedCloumnInfo(info => ({
            ...info,
            [groupName]: {
                checkAll: list.length === cloumnGroupInfo[groupName].length,
                indeterminate: !!list.length && list.length < cloumnGroupInfo[groupName].length,
                checkedList: list
            }
        }));
    }, [setSelectedCloumnInfo, cloumnGroupInfo]);

    const actionConfig = useMemo(() => {
        return {
            submitAction: {
                submitFormAction: 'attributes',
                $onSubmitRequest() {
                    const showCloumns = reduce(Object.keys(selectedCloumnInfo), (obj, itemKey: string) => {
                        if (itemKey !== 'all') {
                            obj = [...obj, ...selectedCloumnInfo[itemKey].checkedList];
                        }
                        return obj;
                    }, [] as string[]);
                    const allCloumns =  reduce(Object.keys(cloumnGroupInfo), (obj, itemKey: string) => {
                        if (itemKey !== 'all') {
                            obj = [...obj, ...cloumnGroupInfo[itemKey]];
                        }
                        return obj;
                    }, [] as any[]);
                    const keys = allCloumns.map(cloumn => {
                        return {
                            ...cloumn,
                            show: showCloumns.includes(cloumn.dataIndex) || showCloumns.includes(cloumn.key)
                        };
                    });
                    setCloumnKeys(keys);
                    onSuccess?.();
                    throw new Error('');
                }
            }
        };
    }, [setCloumnKeys, selectedCloumnInfo, onSuccess, cloumnGroupInfo]);

    const customFunctions = useMemo(() => (
        {
            onChange, onCheckAllChange, setCloumnKeys,
            onReorder: (groupName: string, newOrder: any[]) => {
                // 更新状态或执行其他操作
                setCloumnGroupInfo(prev => ({
                    ...prev,
                    [groupName]: newOrder
                }));
            }
        }
    ), [onChange, onCheckAllChange, setCloumnKeys]);

    const options = useMemo(() => ({
        controls,
        formItemOption: {
            noStyle: true
        },
        actionConfig,
        customFunctions
    }), [actionConfig, customFunctions]);

    const {form, formProps} = useForm(
        ref,
        {...payload, isGroup, cloumnGroupInfo, selectedCloumnInfo},
        options as any
    );

    return (
        <HooksForm
            className='acud-attribute-form'
            {...formProps}
            form={form}
        />
    );
};

export default forwardRef(AttributesForm);
